import React, { createContext, ReactNode, useContext } from 'react';
import {
  CustomFieldProps,
  SmartQuestionRenderer,
} from 'components/stepper/field-components/QuestionnaireField';
import { GetQuestionnaireOutput } from 'graphql/generated/remote-schema-hasura';
import { useQuestionnaireSections } from 'components/questionnaire/hooks/useQuestionnaire';
import { DynamicQuestionnaireDependencie } from 'components/dynamicQuestionnaire/interfaces/dynamicQuestionnaireResponse.interface';
import { useGetDynamicDeps } from 'components/stepper/useStepperForm';
import { FileRejection } from 'react-dropzone';
import { useUploadFileApi } from 'hooks/useUploadFileApi';
import { MBite } from 'const/validation';

import { ContentType } from 'graphql/generated/hasura';
import {
  convertHeicToJpg,
  createAcceptPolicy,
  hasHeicFile,
} from 'utilities/fileHelpers';

export const types = [
  ContentType.Png,
  ContentType.Jpeg,
  ContentType.Jpg,
  ContentType.Heif,
  ContentType.Heic,
];

export const ACCEPTED_FILE_TYPES = createAcceptPolicy(types);
export const MAX_FILE_SIZE = 50;

interface GeneralInfoProps {
  getQuestionnaireOutput: GetQuestionnaireOutput;
  sectionIndex: number;
  BannerComponent?: React.FC;
  customFields?: Partial<Record<string, React.ComponentType<CustomFieldProps>>>;
  hiddenFieldsIds?: string[];
}

interface FileHelperContextType {
  isUploading: boolean;
  onDrop: (
    acceptedFiles: File[],
    fileRejections: FileRejection[],
  ) => Promise<void>;
}

const FileHelperContext = createContext<FileHelperContextType | undefined>(
  undefined,
);

const FileQuestionnaireHelperProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { isUploading, uploadQuestionnaireFile } = useUploadFileApi();

  const validate = (file: File): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!Object.keys(ACCEPTED_FILE_TYPES).includes(file.type)) {
        reject(`Unsupported file format. Allowed formats: ${types.join(', ')}`);
        return;
      }

      if (file.size > MAX_FILE_SIZE * MBite) {
        reject(`File size exceeds ${MAX_FILE_SIZE}MB limit.`);
        return;
      }
      resolve();
    });
  };

  const onDrop = async (
    acceptedFiles: File[],
    fileRejections: FileRejection[],
  ) => {
    let file = acceptedFiles?.[0] || fileRejections?.[0]?.file;

    const isHeic = hasHeicFile(file);

    if (isHeic) {
      file = await convertHeicToJpg(file);
    }

    await validate(file);
    return uploadQuestionnaireFile(file);
  };

  return (
    <FileHelperContext.Provider value={{ onDrop, isUploading }}>
      {children}
    </FileHelperContext.Provider>
  );
};

export const useFileQuestionnaireContext = () => {
  const context = useContext(FileHelperContext);
  if (context === undefined) {
    throw new Error(
      'useFileQuestionnaireContext must be used within a FileQuestionnaireHelperProvider',
    );
  }
  return context;
};

const QuestionnaireSection: React.FC<GeneralInfoProps> = ({
  getQuestionnaireOutput,
  sectionIndex,
  BannerComponent,
  customFields,
  hiddenFieldsIds,
}) => {
  const { questionList, currentSection, questionsById } =
    useQuestionnaireSections(getQuestionnaireOutput, sectionIndex);

  const sectionDependencies = (currentSection?.dependencies ??
    []) as DynamicQuestionnaireDependencie[];

  const dynamicDeps = useGetDynamicDeps(sectionDependencies);

  return (
    <>
      <h6 className="hidden desktop:flex items-baseline font-semibold text-neutral-800 desktop:text-h4 ">
        {currentSection?.name}
        <span className="hidden ml-auto desktop:flex text-sm text-alert-negative font-semibold">
          *Required
        </span>
      </h6>

      <hr className="mt-2.5" />

      {BannerComponent && (
        <div className="pb-2.5">
          <BannerComponent />
        </div>
      )}

      <div className="w-full flex justify-center mt-[30px]">
        <div className="flex flex-col w-[450px] gap-[30px]">
          <FileQuestionnaireHelperProvider>
            <SmartQuestionRenderer
              questionList={questionList}
              dependencies={currentSection?.dependencies ?? []}
              questionsById={questionsById}
              dynamicDeps={dynamicDeps}
              customFields={customFields}
              hiddenFieldsIds={hiddenFieldsIds}
              types={types}
            />
          </FileQuestionnaireHelperProvider>
        </div>
      </div>
    </>
  );
};

export default QuestionnaireSection;
